import type { RouteRecordRaw } from 'vue-router'

/**
 * 合伙人平台路由配置
 * 
 * 10个页面：
 * 1. home - 首页（今日建议 + 企业支持 + 基盘动态）
 * 2. ai - AI助手（智能建议 + 文案生成）
 * 3. tools - 工具清单 + 结果记录
 * 4. base - 我的基盘企业
 * 5. supports - 企业支持（待支持列表）
 * 6. history - 支持记录（已完成）
 * 7. income - 服务收入
 * 8. moments - 基盘瞬间（企业全动态）
 * 9. settings - 我的设置
 * 10. about - 关于/帮助
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/views/partner-site/PartnerPlatform.vue'),
    redirect: '/home',
    children: [
      // ==================== 首页 ====================
      {
        path: 'home',
        name: 'PartnerHome',
        component: () => import('@/views/partner-site/pages/Home.vue'),
        meta: {
          title: '首页',
          icon: 'home',
          permissions: ['*:platform_partner:*:view']
        }
      },

      // ==================== AI助手 ====================
      {
        path: 'ai',
        name: 'PartnerAI',
        component: () => import('@/views/partner-site/pages/AI.vue'),
        meta: {
          title: 'AI助手',
          icon: 'ai',
          permissions: ['*:platform_partner:ai:view']
        }
      },

      // ==================== 工具 ====================
      {
        path: 'tools',
        name: 'PartnerTools',
        component: () => import('@/views/partner-site/pages/Tools.vue'),
        meta: {
          title: '工具',
          icon: 'tools',
          permissions: ['*:platform_partner:tools:view']
        }
      },
      // 工具详情
      {
        path: 'tools/:id',
        name: 'PartnerToolDetail',
        component: () => import('@/views/partner-site/pages/ToolDetail.vue'),
        meta: {
          title: '工具详情',
          icon: 'tools',
          permissions: ['*:platform_partner:tools:view']
        }
      },

      // ==================== 基盘企业 ====================
      {
        path: 'base',
        name: 'PartnerBase',
        component: () => import('@/views/partner-site/pages/Base.vue'),
        meta: {
          title: '我的基盘',
          icon: 'base',
          permissions: ['*:platform_partner:base:view']
        }
      },
      // 基盘企业详情
      {
        path: 'base/:id',
        name: 'PartnerBaseDetail',
        component: () => import('@/views/partner-site/pages/BaseDetail.vue'),
        meta: {
          title: '企业详情',
          icon: 'base',
          permissions: ['*:platform_partner:base:view']
        }
      },

      // ==================== 企业支持 ====================
      {
        path: 'supports',
        name: 'PartnerSupports',
        component: () => import('@/views/partner-site/pages/Supports.vue'),
        meta: {
          title: '企业支持',
          icon: 'support',
          permissions: ['*:platform_partner:support:view']
        }
      },
      // 支持详情
      {
        path: 'supports/:id',
        name: 'PartnerSupportDetail',
        component: () => import('@/views/partner-site/pages/SupportDetail.vue'),
        meta: {
          title: '支持详情',
          icon: 'support',
          permissions: ['*:platform_partner:support:view']
        }
      },

      // ==================== 支持记录 ====================
      {
        path: 'history',
        name: 'PartnerHistory',
        component: () => import('@/views/partner-site/pages/History.vue'),
        meta: {
          title: '支持记录',
          icon: 'history',
          permissions: ['*:platform_partner:support:view']
        }
      },

      // ==================== 服务收入 ====================
      {
        path: 'income',
        name: 'PartnerIncome',
        component: () => import('@/views/partner-site/pages/Income.vue'),
        meta: {
          title: '服务收入',
          icon: 'income',
          permissions: ['*:platform_partner:income:view']
        }
      },

      // ==================== 基盘瞬间 ====================
      {
        path: 'moments',
        name: 'PartnerMoments',
        component: () => import('@/views/partner-site/pages/Moments.vue'),
        meta: {
          title: '基盘瞬间',
          icon: 'moments',
          permissions: ['*:platform_partner:base:view']
        }
      },

      // ==================== 设置 ====================
      {
        path: 'settings',
        name: 'PartnerSettings',
        component: () => import('@/views/partner-site/pages/Settings.vue'),
        meta: {
          title: '设置',
          icon: 'settings',
          permissions: ['*:platform_partner:settings:view']
        }
      },
      // 设置子页面
      {
        path: 'settings/basic',
        name: 'PartnerSettingsBasic',
        component: () => import('@/views/partner-site/pages/SettingsBasic.vue'),
        meta: {
          title: '基本信息',
          icon: 'settings',
          permissions: ['*:platform_partner:settings:view']
        }
      },
      {
        path: 'settings/support',
        name: 'PartnerSettingsSupport',
        component: () => import('@/views/partner-site/pages/SettingsSupport.vue'),
        meta: {
          title: '支持方向',
          icon: 'settings',
          permissions: ['*:platform_partner:settings:view']
        }
      },
      {
        path: 'settings/notification',
        name: 'PartnerSettingsNotification',
        component: () => import('@/views/partner-site/pages/SettingsNotification.vue'),
        meta: {
          title: '通知设置',
          icon: 'settings',
          permissions: ['*:platform_partner:settings:view']
        }
      },
      {
        path: 'settings/level',
        name: 'PartnerSettingsLevel',
        component: () => import('@/views/partner-site/pages/SettingsLevel.vue'),
        meta: {
          title: '我的等级',
          icon: 'settings',
          permissions: ['*:platform_partner:settings:view']
        }
      },

      // ==================== 关于/帮助 ====================
      {
        path: 'about',
        name: 'PartnerAbout',
        component: () => import('@/views/partner-site/pages/About.vue'),
        meta: {
          title: '关于',
          icon: 'about',
          permissions: ['*:platform_partner:*:view']
        }
      },

      // ==================== 消息 ====================
      {
        path: 'messages',
        name: 'PartnerMessages',
        component: () => import('@/views/partner-site/pages/Messages.vue'),
        meta: {
          title: '消息',
          icon: 'message',
          permissions: ['*:platform_partner:*:view']
        }
      },

      // ==================== 人工校验 ====================
      {
        path: 'review',
        name: 'PartnerReviewList',
        component: () => import('@/views/partner-site/pages/ReviewList.vue'),
        meta: {
          title: '人工校验',
          icon: 'review',
          permissions: ['*:platform_partner:review:view']
        }
      },
      {
        path: 'review/:id',
        name: 'PartnerReviewDetail',
        component: () => import('@/views/partner-site/pages/ReviewDetail.vue'),
        meta: {
          title: '校验详情',
          icon: 'review',
          permissions: ['*:platform_partner:review:view']
        }
      }
    ]
  }
]

export const partnerRoutes = routes

export default routes
